import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Upload, Image as ImageIcon, Folder, Search, ExternalLink, Link as LinkIcon } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [urlDialog, setUrlDialog] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newUrlName, setNewUrlName] = useState("");
  const fileRef = useRef();

  const load = async () => {
    const params = new URLSearchParams();
    if (folder) params.set("folder", folder);
    if (q) params.set("q", q);
    const r = await api.get(`/assets?${params.toString()}`);
    setAssets(r.data.assets || []);
    setFolders(r.data.folders || []);
  };
  useEffect(() => { load(); }, [folder, q]);  // eslint-disable-line

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Create asset record
      await api.post("/assets", {
        name: file.name,
        file_id: up.data.id,
        kind: file.type.startsWith("video") ? "video" : "image",
        folder: folder || "",
      });
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addUrl = async () => {
    if (!newUrl) return;
    try {
      await api.post("/assets", {
        name: newUrlName || newUrl,
        url: newUrl,
        kind: /youtube|youtu\.be|vimeo|\.mp4/i.test(newUrl) ? "video" : "url",
        folder: folder || "",
      });
      toast.success("Added");
      setUrlDialog(false); setNewUrl(""); setNewUrlName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeAsset = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try { await api.delete(`/assets/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-7xl text-slate-900">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Media Library</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
              All your <span style={accentFont} className="text-sky-600">assets</span> in one place
            </h1>
            <p className="mt-3 text-sm text-slate-500 max-w-xl">Upload images, videos, link YouTube/Vimeo URLs or hosted PDFs. Use them in any design or playlist.</p>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              hidden
              onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
              data-testid="asset-upload-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm"
              data-testid="upload-asset-btn"
            >
              <Upload className="h-4 w-4 mr-1.5" />{uploading ? "Uploading…" : "Upload"}
            </Button>
            <Dialog open={urlDialog} onOpenChange={setUrlDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" data-testid="add-url-asset-btn">
                  <LinkIcon className="h-4 w-4 mr-1.5" /> Add URL
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 text-slate-900">
                <DialogHeader><DialogTitle style={headingFont}>Add asset from URL</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input value={newUrlName} onChange={(e) => setNewUrlName(e.target.value)} placeholder="Display name" className="bg-slate-50 border-slate-200 text-slate-900" data-testid="url-asset-name"/>
                  <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://… (image, video, PDF, YouTube link)" className="bg-slate-50 border-slate-200 text-slate-900" data-testid="url-asset-url"/>
                  <Button onClick={addUrl} className="w-full rounded-full bg-sky-500 hover:bg-sky-600" data-testid="url-asset-save">Add</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets…"
                   className="pl-9 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-full" data-testid="assets-search"/>
          </div>
          <button onClick={() => setFolder("")} className={`px-3 py-1.5 rounded-full text-xs border ${folder === "" ? "bg-sky-50 border-sky-300 text-sky-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}>All</button>
          {folders.map((f) => (
            <button key={f} onClick={() => setFolder(f)} className={`px-3 py-1.5 rounded-full text-xs border ${folder === f ? "bg-sky-50 border-sky-300 text-sky-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
              <Folder className="h-3 w-3 inline mr-1" />{f}
            </button>
          ))}
        </div>

        {/* Grid */}
        {assets.length === 0 ? (
          <div className="rounded-3xl border border-dashed p-12 text-center" style={{ background: "#F8FAFC", borderColor: "#E2E8F0" }} data-testid="empty-assets">
            <ImageIcon className="h-10 w-10 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2" style={headingFont}>No assets yet</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto mb-5">Upload your first image or video to start building playlists.</p>
            <Button onClick={() => fileRef.current?.click()} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm">
              <Upload className="h-4 w-4 mr-1.5" />Upload first asset
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="assets-grid">
            {assets.map((a) => (
              <div key={a.id} className="rounded-2xl overflow-hidden border bg-white border-slate-200 group" data-testid={`asset-${a.id}`}>
                <div className="aspect-[4/3] bg-slate-50 relative overflow-hidden flex items-center justify-center">
                  {a.kind === "video" ? (
                    <video src={a.url} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={a.url} alt={a.name} loading="lazy" className="w-full h-full object-cover" />
                  )}
                  <div className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] bg-black/60 backdrop-blur px-2 py-0.5 rounded-full text-sky-700">{a.kind}</div>
                </div>
                <div className="p-3 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={headingFont}>{a.name}</div>
                    <div className="text-[11px] text-slate-400 truncate">{a.folder || "Uncategorized"}</div>
                  </div>
                  <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900"><ExternalLink className="h-4 w-4" /></a>
                  <button onClick={() => removeAsset(a.id)} className="text-rose-500 hover:text-rose-700" data-testid={`delete-asset-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
